import React, { useMemo, useRef, useState } from "react";
import { VideoJob, VideoProject } from "../firebase/video";
import VideoCard from "./VideoCard";
import ActionButton, { useActionStatus } from "./ActionButton";
import {
  Download,
  Folder as FolderIcon,
  Film,
  Play,
  Rows3,
  Scissors,
} from "lucide-react";

interface VideoGalleryProps {
  jobs: Array<VideoJob & { id: string }>;
  project: (VideoProject & { id: string }) | null;
  exporting: boolean;
  loading?: boolean;
  canExport?: boolean;
  onRetry: (job: VideoJob) => void;
  onExport: () => Promise<boolean>;
  onCreate?: () => void;
}

function projectStats(jobs: Array<VideoJob & { id: string }>) {
  return {
    total: jobs.length,
    completed: jobs.filter((job) => job.status === "completed").length,
    active: jobs.filter(
      (job) => job.status === "pending" || job.status === "in_progress",
    ).length,
    failed: jobs.filter((job) =>
      ["failed", "expired", "cancelled"].includes(job.status),
    ).length,
  };
}

const STORYBOARD_STATUS: Record<string, string> = {
  pending: "Queued",
  in_progress: "Generating",
  completed: "Ready",
  failed: "Failed",
  expired: "Expired",
  cancelled: "Cancelled",
};

const VideoGallery: React.FC<VideoGalleryProps> = ({
  jobs,
  project,
  exporting,
  loading = false,
  canExport = true,
  onRetry,
  onExport,
  onCreate,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const exportAction = useActionStatus();
  const [playingIndex, setPlayingIndex] = useState(0);
  const stats = useMemo(() => projectStats(jobs), [jobs]);
  const completedJobs = useMemo(
    () => jobs.filter((job) => job.status === "completed" && job.videoUrl),
    [jobs],
  );
  const currentPreviewJob = completedJobs[playingIndex] ?? completedJobs[0];

  const playProject = () => {
    setPlayingIndex(0);
    window.setTimeout(() => videoRef.current?.play().catch(() => {}), 0);
  };

  const handleEnded = () => {
    if (playingIndex + 1 < completedJobs.length) {
      setPlayingIndex((index) => index + 1);
      window.setTimeout(() => videoRef.current?.play().catch(() => {}), 0);
    }
  };

  if (loading) {
    return (
      <section className="video-gallery-panel" role="status" aria-label="Loading storyboard">
        <div className="flex flex-col gap-4 border-b border-line p-4 lg:flex-row lg:items-center lg:justify-between" aria-hidden="true">
          <div className="min-w-0 flex-1">
            <span className="skeleton block h-6 w-48" />
            <div className="mt-3 flex gap-2">
              <span className="skeleton h-6 w-16" />
              <span className="skeleton h-6 w-16" />
            </div>
          </div>
          <div className="flex gap-2">
            <span className="skeleton h-10 w-24" />
            <span className="skeleton h-10 w-24" />
            <span className="skeleton h-10 w-28" />
          </div>
        </div>
        <div className="video-preview-grid" aria-hidden="true">
          <span className="skeleton block aspect-video w-full rounded-lg" />
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2 rounded-lg border border-line p-2">
                <span className="skeleton h-7 w-7 rounded-md" />
                <div className="flex-1">
                  <span className="skeleton block h-3 w-1/2" />
                  <span className="skeleton block h-2.5 w-1/4 mt-1.5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center rounded-lg border border-dashed border-control bg-surface text-center">
        <Film className="w-8 h-8 text-muted mb-3" />
        <h3 className="text-base font-medium text-content">
          Select a project
        </h3>
      </div>
    );
  }
  if (jobs.length === 0 && !project.exportedVideoUrl) {
    return <section className="video-empty-gallery">
      <div className="video-empty-project"><FolderIcon /><div><h2>{project.title}</h2><span>0 clips</span></div></div>
      <div className="video-empty-stage"><div className="film-illustration" aria-hidden="true"><div /><div><Play size={28} strokeWidth={1.2} /></div><div /></div><h3>No clips yet</h3>{onCreate && <button type="button" className="button-secondary" onClick={onCreate}><Film size={16} />Create clip</button>}</div>
    </section>;
  }

  return (
    <div className="space-y-5 motion-fade">
      <section className="video-gallery-panel">
        <div className="flex flex-col gap-4 border-b border-line p-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="truncate text-xl font-semibold text-content">
              {project.title}
            </h2>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted">
              <span className="rounded-md bg-raised px-2 py-1">
                {stats.total} clips
              </span>
              <span className="rounded-md bg-success-soft px-2 py-1 text-success">
                {stats.completed} ready
              </span>
              {stats.active > 0 && (
                <span className="rounded-md bg-accent-soft px-2 py-1 text-accent">
                  {stats.active} active
                </span>
              )}
              {stats.failed > 0 && (
                <span className="rounded-md bg-danger-soft px-2 py-1 text-danger">
                  {stats.failed} needs attention
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {onCreate && <button type="button" className="button-secondary" onClick={onCreate}><Film size={15} />New clip</button>}
            <button
              type="button"
              onClick={playProject}
              disabled={completedJobs.length === 0}
              className="inline-flex items-center gap-1.5 rounded-xl border border-line px-3.5 py-2 text-sm font-semibold text-content hover:bg-subtle disabled:opacity-50 transition-colors"
            >
              <Play size={15} />
              Play All
            </button>
            <ActionButton
              onClick={() => { exportAction.run(onExport).catch(() => {}); }}
              disabled={!canExport || completedJobs.length === 0 || exporting}
              title={
                canExport
                  ? "Stitch completed clips into one MP4"
                  : "Create or select a project to export a stitched MP4"
              }
              status={exporting ? "pending" : exportAction.status}
              icon={<Scissors size={15} />}
              label="Export MP4"
              pendingLabel="Exporting…"
              successLabel="Exported"
              errorLabel="Export failed"
              className="rounded-md bg-action hover:bg-action-hover active:translate-y-px px-3.5 py-2 text-sm font-semibold text-on-solid shadow-xs disabled:opacity-50 transition-[background-color,transform] duration-150"
            />
          </div>
        </div>

        <div className="video-preview-grid">
          <div className="relative aspect-video overflow-hidden rounded-lg bg-media">
            {currentPreviewJob?.videoUrl ? (
              <video
                ref={videoRef}
                key={currentPreviewJob.id}
                src={currentPreviewJob.videoUrl}
                controls
                preload="metadata"
                onEnded={handleEnded}
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-on-media">
                <Film className="mr-2 h-5 w-5" />
                No completed clips
              </div>
            )}
          </div>

          <div className="space-y-3">
            {project.exportedVideoUrl && (
              <a
                href={project.exportedVideoUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-lg border border-success-line bg-success-soft px-3 py-2 text-sm font-medium text-success hover:bg-success-soft"
              >
                Exported video
                <Download size={15} />
              </a>
            )}
            {project.exportStatus === "failed" && project.exportError && (
              <div className="rounded-lg border border-danger-line bg-danger-soft px-3 py-2 text-sm text-danger">
                {project.exportError}
              </div>
            )}
            {project.exportWarning && (
              <div className="rounded-lg border border-warning-line bg-warning-soft px-3 py-2 text-sm text-warning">
                {project.exportWarning}
              </div>
            )}
            {!canExport && (
              <div className="rounded-lg border border-accent-line bg-accent-soft px-3 py-2 text-sm text-accent">
                Unassigned clips stay available here. Use Assign unassigned
                clips in the Projects panel to group them for MP4 export.
              </div>
            )}

            <div className="flex items-center gap-2 text-sm font-semibold text-content">
              <Rows3 size={15} className="text-muted" />
              Storyboard
            </div>
            <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
              {jobs.length === 0 ? (
                <div className="rounded-lg border border-dashed border-control p-4 text-center text-sm text-muted">
                  Generate a clip to start the storyboard.
                </div>
              ) : (
                jobs.map((job, index) => {
                  const completedIndex = completedJobs.findIndex(
                    (completedJob) => completedJob.id === job.id,
                  );
                  const playable = completedIndex >= 0;
                  const isCurrent = playable && currentPreviewJob?.id === job.id;
                  const rowContent = (
                    <>
                      <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md font-mono tabular-nums text-xs font-semibold transition-colors duration-150 ${isCurrent ? "bg-action text-on-solid" : "bg-surface text-muted"}`}>
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-content">
                          {job.sceneTitle || `Shot ${index + 1}`}
                        </div>
                        <div className={`truncate text-xs ${job.status === "failed" || job.status === "expired" ? "text-danger" : "text-muted"}`}>
                          {STORYBOARD_STATUS[job.status] ?? job.status.replace("_", " ")}
                          {job.duration ? <span className="tabular-nums"> · {job.duration}s</span> : null}
                          {isCurrent ? " · In preview" : ""}
                        </div>
                      </div>
                    </>
                  );
                  return playable ? (
                    <button
                      key={job.id}
                      type="button"
                      aria-pressed={isCurrent}
                      aria-label={`Preview ${job.sceneTitle || `shot ${index + 1}`}`}
                      onClick={() => setPlayingIndex(completedIndex)}
                      className={`flex w-full items-center gap-2 rounded-lg border p-2 text-left transition-colors duration-150 focus-visible:outline-offset-2 ${isCurrent ? "border-accent-line bg-accent-soft" : "border-line bg-subtle hover:bg-raised hover:border-control active:bg-pressed/70"}`}
                    >
                      {rowContent}
                    </button>
                  ) : (
                    <div
                      key={job.id}
                      className="flex w-full items-center gap-2 rounded-lg border border-dashed border-line bg-surface p-2 text-left"
                    >
                      {rowContent}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>

      {jobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-control bg-surface py-16 text-center">
          <div className="w-14 h-14 bg-raised rounded-full flex items-center justify-center mb-4">
            <Film className="w-7 h-7 text-muted" />
          </div>
          <h3 className="text-lg font-medium text-content mb-1">
            No clips yet
          </h3>
          <p className="text-sm text-muted max-w-xs">
            Create the first shot here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <VideoCard key={job.id} job={job} onRetry={onRetry} />
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoGallery;
